import { StockReorderButton } from "@/components/stock-tracking/StockReorderButton";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { useProfile } from "@/queries/customer/customer";
import { useStockTracking } from "@/queries/customer/stock-tracking";
import { useCustomerOrderStats } from "@/queries/orders/orders";
import { formatSavings } from "@/queries/orders/order-insights";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { toast } from "@medusajs/ui";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Clock,
  DollarSign,
  Sparkles,
  Truck,
  Zap,
  type LucideProps
} from "lucide-react";

type IconType = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

interface StatsBoxProps {
  title: string;
  value: string | number;
  description: string;
  Icon: IconType;
  loading?: boolean;
  className?: string;
}

/**
 * A single hero stat card. Presentational only - wrap it in a `<Link>` at the
 * call site when the stat should navigate somewhere.
 */
function StatsBox({
  title,
  value,
  description,
  Icon,
  loading,
  className
}: StatsBoxProps) {
  return (
    <div
      aria-label={title}
      className={cn(
        "flex h-full flex-col justify-between rounded-lg bg-white/10 p-4 text-white backdrop-blur-sm transition-colors",
        className
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <Icon className="h-5 w-5 flex-shrink-0 opacity-80" aria-hidden="true" />
        {loading ? (
          <Skeleton className="h-7 w-14 bg-white/20" />
        ) : (
          <TruncateTooltip as="span" className="text-2xl font-bold">
            {value}
          </TruncateTooltip>
        )}
      </div>
      <p className="text-xs opacity-80">{description}</p>
    </div>
  );
}

export function WelcomePanel() {
  const { data: stats, isLoading: statsLoading } = useCustomerOrderStats();
  const { data: profile } = useProfile();
  const { data: stock, isLoading: stockLoading } = useStockTracking();

  const firstName = profile?.customer?.first_name;
  // Same stats query (and `isActive` definition) as the /orders "Active Orders"
  // card, so the two pages always show the same number (KAN-407).
  const activeCount = stats?.active ?? 0;
  const dueCount = stock?.due_count ?? 0;
  // The tracked items currently due - the hero "Quick Reorder" fans these out
  // across the right seller cart(s) via api#152. Same query as the stat + widget.
  const dueItems = (stock?.stock_items ?? []).filter((item) => item.is_due);
  const dueVariantIds = dueItems.map((item) => item.variant_id);

  const savingsValue = formatSavings(stats) ?? "-";

  const inTransitCount = stats?.inTransitCount ?? 0;

  return (
    <div className="bg-gradient-to-r from-violet-600 via-rose-500 to-amber-500 px-6 py-6">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col items-center justify-between gap-8 md:flex-row md:items-start">
          <div className="flex-1 text-white">
            <div className="mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              {/* Names the person: masked out of session replays (ADR-0006). */}
              <span
                className={cn("text-sm font-medium opacity-90", PII_MASK_CLASS)}
              >
                {firstName ? `Welcome back, ${firstName}` : "Welcome back"}
              </span>
            </div>
            <h1 className="mb-4 text-3xl font-bold lg:text-4xl">
              Your Salon Supply
              <br />
              <span className="text-amber-200">Command Center</span>
            </h1>
            <p className="mb-6 text-sm opacity-90">
              Smart reordering • Best prices • Trusted suppliers
            </p>
            <div className="flex flex-wrap gap-3">
              {dueVariantIds.length > 0 ? (
                // Real primary action (#73): reorder everything currently due
                // across the right seller cart(s), review the changed lines,
                // then route to the cart.
                <StockReorderButton
                  variantIds={dueVariantIds}
                  items={dueItems}
                  source="hero"
                  navigateToCartOnComplete
                  className="bg-white/20 text-white hover:bg-white hover:text-violet-700"
                  icon={<Zap className="mr-1 h-3 w-3" aria-hidden="true" />}
                  label="Quick Reorder"
                />
              ) : (
                // Nothing due - a friendly nudge instead of an empty cart hop.
                <Button
                  type="button"
                  className="bg-white/20 text-white hover:bg-white hover:text-violet-700"
                  onClick={() =>
                    toast.info(
                      "You're all stocked up - nothing to reorder right now"
                    )
                  }
                >
                  <Zap className="mr-1 h-3 w-3" aria-hidden="true" /> Quick
                  Reorder
                </Button>
              )}
              <Button
                className="bg-white/20 text-white hover:bg-white hover:text-violet-700"
                asChild
              >
                <Link to="/orders">View All Orders</Link>
              </Button>
            </div>
          </div>

          {/*
           * Honest hero stat grid (storefront#76 + #73). A 2x2 of real stats:
           * active orders, savings, next delivery, and "Due to Reorder" (opt-in
           * tracked items at/below level, api#148). Every value maps to real
           * order / stock data - no fabricated demo numbers.
           */}
          <div className="grid w-full max-w-sm grid-cols-2 items-stretch gap-3 md:w-80">
            <Link
              to="/orders"
              aria-label="View active orders"
              className="block"
            >
              <StatsBox
                title="Active Orders"
                value={activeCount}
                Icon={Clock}
                description="active orders"
                loading={statsLoading}
                className="hover:bg-white/20"
              />
            </Link>

            <StatsBox
              title="Savings"
              value={savingsValue}
              Icon={DollarSign}
              // Like the tiles beside it, a zero gets its own words rather than
              // "$0.00 saved on your orders" (the header chip hides it, KAN-305).
              // The figure only sums order discounts, hence "discounts". Past
              // the sampled window the zero is only of the recent orders.
              description={
                stats && !stats.totalSaved
                  ? stats.approximate
                    ? "no recent discounts"
                    : "no discounts yet"
                  : "saved on your orders"
              }
              loading={statsLoading}
            />

            <Link
              to="/orders"
              search={{ fulfillment: "in_transit" }}
              aria-label="View orders in transit"
              className="block"
            >
              <StatsBox
                title="Next Delivery"
                value={inTransitCount}
                Icon={Truck}
                description={
                  inTransitCount > 0 ? "in transit" : "nothing in transit"
                }
                loading={statsLoading}
                className="hover:bg-white/20"
              />
            </Link>

            <Link
              to="/account/stock"
              aria-label="Review items due to reorder"
              className="block"
            >
              <StatsBox
                title="Due to Reorder"
                value={dueCount}
                Icon={AlertTriangle}
                description={
                  dueCount > 0 ? "items to reorder" : "all stocked up"
                }
                loading={stockLoading}
                className="hover:bg-white/20"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
